import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { useTheme } from '../../../contexts/ThemeContext';
import Card from '../../common/Card.jsx';
import Pagination from '../../common/Pagination.jsx';
import ArticlesList from './ArticlesList.jsx';
import AddArticleModal from './AddArticleModal.jsx';
import EditArticleModal from './EditArticleModal.jsx';
import ViewArticleModal from './ViewArticleModal.jsx';
import DeleteArticleModal from './DeleteArticleModal.jsx';
import articleService from '../../../services/articleService';
import clientService from '../../../services/clientService';
import localService from '../../../services/localService';
import ClientSelectDropdown from '../../common/ClientSelectDropdown';
import LocalSelectDropdown from '../../common/LocalSelectDropdown';
import { toast } from 'react-toastify';
import { openBarcodeScanner } from '../../../utils/barcodeScanner';

const Articles = () => {
  const { isDarkMode } = useTheme();
  const { user } = useAuth();

  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [exporting, setExporting] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 10,
    total: 0,
    maxPage: 0
  });

  // Filter states
  const [filters, setFilters] = useState({
    searchTerms: '',
    LocalId: '',
    ClientId: '',
    CodeABarre: '',
    NumeroDeSerie: ''
  });
  const [showFilters, setShowFilters] = useState(false);
  const [filtersApplied, setFiltersApplied] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [showMobileActions, setShowMobileActions] = useState(false);

  const [clients, setClients] = useState([]);
  const [clientsLoading, setClientsLoading] = useState(false);
  const [locaux, setLocaux] = useState([]);
  const [locauxLoading, setLocauxLoading] = useState(false);

  // Camera scanning states
  const [barcodeScanning, setBarcodeScanning] = useState(false);
  const scannerRef = useRef(null);

  // Stop scanning function
  const stopScanning = useCallback(() => {
    if (scannerRef.current) {
      scannerRef.current.cleanup();
      scannerRef.current = null;
    }
    setBarcodeScanning(false);
  }, []);

  // Start barcode scanning using Quagga2 (works on iOS + Android)
  const startBarcodeScanning = () => {
    setBarcodeScanning(true);

    const scanner = openBarcodeScanner({
      modalId: 'barcode-scanner-modal',
      scannerId: 'quagga-scanner',
      onDetected: (barcode) => {
        scannerRef.current = null;
        setBarcodeScanning(false);

        setFilters(prev => ({ ...prev, CodeABarre: barcode }));
        toast.success(`✅ Code vérifié: ${barcode}`, { autoClose: 2000 });

        setTimeout(() => {
          const newFilters = { ...filters, CodeABarre: barcode };
          setFiltersApplied(Object.values(newFilters).some(value => {
            if (typeof value === 'string') return value.trim() !== '';
            return value !== '' && value != null;
          }));
          loadArticles(pagination.pageSize, 1, newFilters);
        }, 300);
      },
      onError: () => {
        setBarcodeScanning(false);
        const userInput = prompt('Caméra non disponible. Saisissez le code-barres manuellement:');
        if (userInput && userInput.trim()) {
          const trimmedInput = userInput.trim();
          if (trimmedInput.length <= 8) {
            setFilters(prev => ({ ...prev, CodeABarre: trimmedInput }));
            toast.success(`✅ Code saisi: ${trimmedInput}`);
            setTimeout(() => {
              const newFilters = { ...filters, CodeABarre: trimmedInput };
              setFiltersApplied(true);
              loadArticles(pagination.pageSize, 1, newFilters);
            }, 300);
          } else {
            toast.error('❌ Code invalide! Maximum 8 chiffres.');
          }
        }
      },
      onManualEntry: () => {
        scannerRef.current = null;
        setBarcodeScanning(false);
        const userInput = prompt('Saisissez le code-barres manuellement (max 8 chiffres):');
        if (userInput && userInput.trim()) {
          const trimmedInput = userInput.trim();
          if (trimmedInput.length > 8) {
            toast.error('❌ Code invalide! Maximum 8 chiffres.');
            return;
          }
          setFilters(prev => ({ ...prev, CodeABarre: trimmedInput }));
          toast.success(`✅ Code saisi: ${trimmedInput}`);
          setTimeout(() => {
            const newFilters = { ...filters, CodeABarre: trimmedInput };
            setFiltersApplied(true);
            loadArticles(pagination.pageSize, 1, newFilters);
          }, 300);
        }
      },
    });
    scannerRef.current = scanner;
  };

  // Load articles on component mount


  useEffect(() => {
    const fetchClients = async () => {
      setClientsLoading(true);
      try {
        const result = await clientService.getClients(1000, 1, {});
        if (result.success && Array.isArray(result.data.list)) {
          setClients(result.data.list);
        } else {
          setClients([]);
        }
      } catch {
        setClients([]);
      } finally {
        setClientsLoading(false);
      }
    };
    fetchClients();
  }, []);

  // Load locaux for filter (optionally filtered by client)
  useEffect(() => {
    const fetchLocaux = async () => {
      setLocauxLoading(true);
      try {
        const filterObj = {};
        if (filters.ClientId) filterObj.clientId = filters.ClientId;
        const result = await localService.getLocaux(1000, 1, filterObj);
        if (result.success && Array.isArray(result.data.list)) {
          setLocaux(result.data.list);
        } else {
          setLocaux([]);
        }
      } catch {
        setLocaux([]);
      } finally {
        setLocauxLoading(false);
      }
    };
    fetchLocaux();
  }, [filters.ClientId]);

  // Load articles from API with filters
  const loadArticles = async (pageSize = pagination.pageSize, page = pagination.page, filterParams = filters) => {
    setLoading(true);
    setError('');

    try {
      // Prepare filter parameters for API
      const apiFilters = {};
      if (filterParams.searchTerms && typeof filterParams.searchTerms === 'string' && filterParams.searchTerms.trim()) {
        apiFilters.searchTerms = filterParams.searchTerms.trim();
      }
      if (filterParams.LocalId && filterParams.LocalId !== '') {
        apiFilters.LocalId = filterParams.LocalId;
      }
      if (filterParams.ClientId && filterParams.ClientId !== '') {
        apiFilters.ClientId = filterParams.ClientId;
      }
      if (filterParams.CodeABarre && typeof filterParams.CodeABarre === 'string' && filterParams.CodeABarre.trim()) {
        apiFilters.CodeABarre = filterParams.CodeABarre.trim();
      }
      if (filterParams.NumeroDeSerie && typeof filterParams.NumeroDeSerie === 'string' && filterParams.NumeroDeSerie.trim()) {
        apiFilters.NumeroDeSerie = filterParams.NumeroDeSerie.trim();
      }

      const result = await articleService.getArticles(pageSize, page, apiFilters);

      if (result.success) {
        setArticles(result.data.list);
        setPagination({
          page: result.data.page,
          pageSize: result.data.pageSize,
          total: result.data.total,
          maxPage: result.data.maxPage
        });
      } else {
        setError(result.message);
      }
    } catch (error) {
      console.error('Articles loading error:', error);
      setError('Erreur lors du chargement des articles');
    } finally {
      setLoading(false);
    }
  };

  // Export functionality - SIMPLIFIED to use existing table data only
  const handleExport = (format = 'csv') => {
    if (!articles || articles.length === 0) {
      setError('Aucune donnée à exporter');
      return;
    }

    setExporting(true);

    try {
      // Use existing articles data (what's currently displayed in the table)
      if (format === 'csv') {
        exportToCSV(articles);
      } else if (format === 'excel') {
        exportToExcel(articles);
      } else if (format === 'json') {
        exportToJSON(articles);
      }
    } catch (error) {
      console.error('Export error:', error);
      setError('Erreur lors de l\'exportation des données');
    } finally {
      setExporting(false);
    }
  };

  // Export to CSV - Same as before but using existing data
  const exportToCSV = (data) => {
    const headers = [
      'ID',
      'Désignation',
      'Type',
      'Famille',
      'Capacité (kg)',
      'Marque',
      'Code à barres',
      'Numéro de série',
      'Emplacement',
      'Date de fabrication',
      'Client',
      'Local',
      'Responsable',
      'Téléphone responsable',
      'Observations'
    ];

    const csvContent = [
      headers.join(','),
      ...data.map(article => [
        article.id || '',
        `"${(article.designation || '').replace(/"/g, '""')}"`,
        `"${(article.typeArticle?.designation || '').replace(/"/g, '""')}"`,
        `"${(article.typeArticle?.famille?.designation || '').replace(/"/g, '""')}"`,
        article.capacite || '',
        `"${(article.marque || '').replace(/"/g, '""')}"`,
        `"${(article.codeABarre || '').replace(/"/g, '""')}"`,
        `"${(article.numeroSerie || '').replace(/"/g, '""')}"`,
        `"${(article.emplacement || '').replace(/"/g, '""')}"`,
        article.datetimeFabrication ? new Date(article.datetimeFabrication).toLocaleDateString('fr-FR') : '',
        `"${(article.client?.raisonSocial || '').replace(/"/g, '""')}"`,
        `"${(article.local?.designation || '').replace(/"/g, '""')}"`,
        `"${(article.local?.responsable || '').replace(/"/g, '""')}"`,
        `"${(article.local?.telResponsable || '').replace(/"/g, '""')}"`,
        `"${(article.observation || '').replace(/"/g, '""')}"`
      ].join(','))
    ].join('\n');

    const timestamp = new Date().toISOString().slice(0, 10);
    downloadFile(csvContent, `articles-export-${timestamp}.csv`, 'text/csv;charset=utf-8;');
  };

  // Export to JSON - Same as before but using existing data
  const exportToJSON = (data) => {
    const exportData = {
      exportDate: new Date().toISOString(),
      totalRecords: data.length,
      pagination: {
        currentPage: pagination.page,
        pageSize: pagination.pageSize,
        totalPages: pagination.maxPage,
        totalRecords: pagination.total
      },
      filters: filtersApplied ? filters : null,
      data: data
    };

    const jsonContent = JSON.stringify(exportData, null, 2);
    const timestamp = new Date().toISOString().slice(0, 10);
    downloadFile(jsonContent, `articles-export-${timestamp}.json`, 'application/json');
  };

  // Export to Excel - Same as before but using existing data
  const exportToExcel = (data) => {
    const headers = [
      'ID',
      'Désignation',
      'Type',
      'Famille',
      'Capacité (kg)',
      'Marque',
      'Code à barres',
      'Numéro de série',
      'Emplacement',
      'Date de fabrication',
      'Client',
      'Local',
      'Responsable',
      'Téléphone responsable',
      'Observations'
    ];

    // Excel-compatible CSV with BOM for proper encoding
    const BOM = '\uFEFF';
    const csvContent = BOM + [
      headers.join('\t'),
      ...data.map(article => [
        article.id || '',
        (article.designation || '').replace(/\t/g, ' '),
        (article.typeArticle?.designation || '').replace(/\t/g, ' '),
        (article.typeArticle?.famille?.designation || '').replace(/\t/g, ' '),
        article.capacite || '',
        (article.marque || '').replace(/\t/g, ' '),
        (article.codeABarre || '').replace(/\t/g, ' '),
        (article.numeroSerie || '').replace(/\t/g, ' '),
        (article.emplacement || '').replace(/\t/g, ' '),
        article.datetimeFabrication ? new Date(article.datetimeFabrication).toLocaleDateString('fr-FR') : '',
        (article.client?.raisonSocial || '').replace(/\t/g, ' '),
        (article.local?.designation || '').replace(/\t/g, ' '),
        (article.local?.responsable || '').replace(/\t/g, ' '),
        (article.local?.telResponsable || '').replace(/\t/g, ' '),
        (article.observation || '').replace(/\t/g, ' ')
      ].join('\t'))
    ].join('\n');

    const timestamp = new Date().toISOString().slice(0, 10);
    downloadFile(csvContent, `articles-export-${timestamp}.xlsx`, 'application/vnd.ms-excel');
  };

  // Download file utility - Enhanced
  const downloadFile = (content, filename, mimeType) => {
    try {
      const blob = new Blob([content], { type: mimeType });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      // Show success message
      setTimeout(() => {
        // You can add a toast notification here if you have one
        console.log(`Export réussi: ${filename}`);
      }, 100);
    } catch (error) {
      console.error('Download error:', error);
      setError('Erreur lors du téléchargement du fichier');
    }
  };

  // Load articles on component mount
  useEffect(() => {
    loadArticles();
  }, []);

  // Full right-content loading indicator while API responds
  if (loading) {
    return (
      <div className="p-4 sm:p-6 min-h-screen flex items-start justify-center">
        <div className="w-full max-w-6xl">
          <div className={`rounded-2xl shadow-sm border p-8 flex flex-col items-center justify-center ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`} style={{ minHeight: '60vh' }}>
            <div className={`animate-spin rounded-full h-12 w-12 border-b-2 ${isDarkMode ? 'border-primary-400' : 'border-primary-600'}`} />
            <div className={`mt-4 text-lg font-semibold ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>Chargement des articles...</div>
            <div className={`mt-2 text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Veuillez patienter pendant la récupération des données.</div>
          </div>
        </div>
      </div>
    );
  }

  // Handle page change
  const handlePageChange = (newPage) => {
    loadArticles(pagination.pageSize, newPage, filters);
  };

  // Handle page size change
  const handlePageSizeChange = (newPageSize) => {
    loadArticles(newPageSize, 1, filters);
  };

  // Handle filter change
  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Apply filters - KEEP FILTERS OPEN when called from scanner
  const applyFilters = (keepFiltersOpen = false) => {
    setFiltersApplied(Object.values(filters).some(value => {
      if (typeof value === 'string') {
        return value.trim() !== '';
      }
      return value !== '' && value != null;
    }));
    loadArticles(pagination.pageSize, 1, filters);
    if (!keepFiltersOpen) {
      setShowFilters(false);
    }
  };

  // Clear filters
  const clearFilters = () => {
    const clearedFilters = {
      searchTerms: '',
      LocalId: '',
      ClientId: '',
      CodeABarre: '',
      NumeroDeSerie: ''
    };
    setFilters(clearedFilters);
    setFiltersApplied(false);
    loadArticles(pagination.pageSize, 1, clearedFilters);
    setShowFilters(false);
  };

  // Modal handlers
  const handleView = (article) => {
    setSelectedArticle(article);
    setShowViewModal(true);
  };

  const handleEdit = (article) => {
    setSelectedArticle(article);
    setShowEditModal(true);
  };

  const handleDelete = (article) => {
    setSelectedArticle(article);
    setShowDeleteModal(true);
  };

  const handleRefresh = () => {
    loadArticles(pagination.pageSize, pagination.page, filters);
  };

  // Export Excel by client handler
  const handleExportExcelByClient = async (clientId) => {
    setExporting(true);
    setError('');
    try {
      const res = await articleService.exportArticlesExcelByClient(clientId);
      if (res.success && res.data) {
        // Try to get filename from content-disposition header
        let filename = `articles-client-${clientId}.xlsx`;
        if (res.filename) {
          const match = /filename="?([^";]+)"?/i.exec(res.filename);
          if (match) filename = match[1];
        }
        // Create a blob and trigger download
        const url = window.URL.createObjectURL(new Blob([res.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      } else {
        setError(res.message || 'Erreur lors de l\'export Excel');
      }
    } catch (err) {
      setError('Erreur lors de l\'export Excel');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="space-y-4 sm:space-y-6 p-4 sm:p-6">
      {/* Page Header - Responsive */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="min-w-0 flex-1">
          <h1 className={`text-2xl sm:text-3xl font-bold truncate ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
            Articles
          </h1>
          <p className={`text-sm sm:text-base mt-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
            Gestion des extincteurs et équipements de sécurité
            {filtersApplied && (
              <span className={`font-medium ${isDarkMode ? 'text-dark-primary-400' : 'text-primary-600'}`}> (Filtré)</span>
            )}
          </p>
        </div>

        {/* Desktop Actions */}
        <div className="hidden lg:flex space-x-3 flex-shrink-0">
          <button
            onClick={handleRefresh}
            disabled={loading}
            className="bg-white border border-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-50 transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <i className={`fas fa-sync-alt mr-2 ${loading ? 'animate-spin' : ''}`}></i>
            Actualiser
          </button>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`border px-4 py-2 rounded-lg transition-colors text-sm font-medium ${filtersApplied
              ? 'bg-primary-50 border-primary-300 text-primary-700'
              : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
              }`}
          >
            <i className="fas fa-filter mr-2"></i>
            Filtrer
            {filtersApplied && <span className="ml-1 bg-primary-500 text-white rounded-full px-1.5 py-0.5 text-xs">•</span>}
          </button>

<div className="relative group">
            <button
              disabled={exporting || articles.length === 0}
              className="bg-white border border-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-50 transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <i className={`fas fa-download mr-2 ${exporting ? 'animate-pulse' : ''}`}></i>
              {exporting ? 'Export...' : `Exporter (${articles.length})`}
              <i className="fas fa-chevron-down ml-1 text-xs"></i>
            </button>
            {/* Export Options Dropdown */}
            <div className="absolute right-0 mt-1 w-56 bg-white rounded-lg shadow-lg border border-gray-200 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-20">
              <div className="py-2">
                
                {/* Excel by client: admin can choose, client uses own id */}
                {user?.isAdmin ? (
                  <div className="px-4 py-2">
                    <label className="block text-xs font-medium text-gray-700 mb-1">Exporter Excel par client</label>
                
                    <select
                      className={`w-full px-2 py-1 border rounded focus:ring-2 focus:border-transparent text-sm ${
                        isDarkMode
                          ? 'bg-gray-800 border-gray-700 text-gray-200 disabled:bg-gray-900 disabled:text-gray-500'
                          : 'bg-white border-gray-300 text-gray-900 disabled:bg-gray-100 disabled:text-gray-500'
                      }`}
                      disabled={clientsLoading || exporting}
                      onChange={e => {
                        if (e.target.value) handleExportExcelByClient(e.target.value);
                        e.target.selectedIndex = 0; // reset
                      }}
                    >
                      <option value="" className={isDarkMode ? 'bg-gray-800' : 'bg-white'}>
                        Sélectionner un client...
                      </option>
                      {clients.map(c => (
                        <option 
                          key={c.id} 
                          value={c.id}
                          className={isDarkMode ? 'bg-gray-800' : 'bg-white'}
                        >
                          {c.raisonSocial || `Client #${c.id}`}
                        </option>
                      ))}
                    </select>
                  </div>
                ) : (
                  <button
                    onClick={() => handleExportExcelByClient(user?.idUser)}
                    disabled={exporting || !user?.idUser}
                    className="w-full text-left px-4 py-2 text-sm text-primary-700 hover:bg-primary-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <i className="fas fa-file-excel mr-2 text-green-700"></i>
                    Exporter Excel (mes articles)
                  </button>
                )}
              </div>
            </div>
          </div>

          {user?.isAdmin && (
            <button
              onClick={() => setShowAddModal(true)}
              className={`px-4 py-2 rounded-lg transition-all text-sm font-medium ${isDarkMode
                ? 'bg-dark-primary-600 text-white hover:bg-dark-primary-700'
                : 'bg-primary-600 text-white hover:bg-primary-700'
                }`}
            >
              <i className="fas fa-plus mr-2"></i>
              Nouvel Article
            </button>
          )}
        </div>

        {/* Mobile/Tablet Actions - Updated */}
        <div className="lg:hidden">
          <div className="flex items-center justify-end space-x-2">
            {user?.isAdmin && (
              <button
                onClick={() => setShowAddModal(true)}
                className="bg-primary-600 text-white px-3 py-2 rounded-lg hover:bg-primary-700 transition-all text-sm font-medium flex-1 sm:flex-none"
              >
                <i className="fas fa-plus mr-1 sm:mr-2"></i>
                <span className="hidden xs:inline">Nouvel </span>Article
              </button>
            )}

            <div className="relative">
              <button
                onClick={() => setShowMobileActions(!showMobileActions)}
                className="bg-white border border-gray-300 text-gray-700 p-2 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <i className="fas fa-ellipsis-v"></i>
              </button>

              {showMobileActions && (
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 z-10">
                  <div className="py-2">
                    <button
                      onClick={() => {
                        handleRefresh();
                        setShowMobileActions(false);
                      }}
                      disabled={loading}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
                    >
                      <i className={`fas fa-sync-alt mr-2 w-4 ${loading ? 'animate-spin' : ''}`}></i>
                      Actualiser
                    </button>
                    <button
                      onClick={() => {
                        setShowFilters(!showFilters);
                        setShowMobileActions(false);
                      }}
                      className={`w-full text-left px-4 py-2 text-sm transition-colors ${filtersApplied
                        ? 'text-primary-700 bg-primary-50'
                        : 'text-gray-700 hover:bg-gray-50'
                        }`}
                    >
                      <i className="fas fa-filter mr-2 w-4"></i>
                      Filtrer
                      {filtersApplied && <span className="ml-1 bg-primary-500 text-white rounded-full px-1.5 py-0.5 text-xs">•</span>}
                    </button>

                    <div className="border-t border-gray-100 my-1"></div>
                    
                    <div className="border-t border-gray-100 my-1"></div>
                    {/* Excel by client: admin can choose, client uses own id */}
                    {user?.isAdmin ? (
                      <div className="px-4 py-2">
                        <label className="block text-xs font-medium text-gray-700 mb-1">Exporter Excel par client</label>
                        <select
                          className={`w-full px-2 py-1 border rounded focus:ring-2 focus:border-transparent text-sm ${isDarkMode
                              ? 'bg-gray-800 border-gray-700 text-gray-200 disabled:bg-gray-900 disabled:text-gray-500'
                              : 'bg-white border-gray-300 text-gray-900 disabled:bg-gray-100 disabled:text-gray-500'
                            }`}
                          disabled={clientsLoading || exporting}
                          onChange={e => {
                            if (e.target.value) handleExportExcelByClient(e.target.value);
                            e.target.selectedIndex = 0; // reset
                            setShowMobileActions(false);
                          }}
                        >
                          <option value="" className={isDarkMode ? 'bg-gray-800' : 'bg-white'}>
                            Sélectionner un client...
                          </option>
                          {clients.map(c => (
                            <option
                              key={c.id}
                              value={c.id}
                              className={isDarkMode ? 'bg-gray-800' : 'bg-white'}
                            >
                              {c.raisonSocial || `Client #${c.id}`}
                            </option>
                          ))}
                        </select>
                      </div>
                    ) : (
                      <button
                        onClick={() => {
                          handleExportExcelByClient(user?.idUser);
                          setShowMobileActions(false);
                        }}
                        disabled={exporting || !user?.idUser}
                        className="w-full text-left px-4 py-2 text-sm text-primary-700 hover:bg-primary-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <i className="fas fa-file-excel mr-2 text-green-700"></i>
                        Exporter Excel (mes articles)
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Error Message - Responsive */}
      {error && (
        <div className={`border px-4 py-3 sm:px-6 sm:py-4 rounded-lg ${isDarkMode
          ? 'bg-red-900/50 border-red-800 text-red-300'
          : 'bg-red-50 border-red-200 text-red-700'
          }`}>
          <div className="flex items-start">
            <i className="fas fa-exclamation-circle mr-2 mt-0.5 flex-shrink-0"></i>
            <span className="text-sm sm:text-base">{error}</span>
          </div>
        </div>
      )}

      {/* Export Success Message - Simplified */}
      {exporting && (
        <div className="bg-blue-50 border border-blue-200 text-blue-700 px-4 py-3 sm:px-6 sm:py-4 rounded-lg">
          <div className="flex items-center">
            <i className="fas fa-download mr-2 animate-pulse"></i>
            <span className="text-sm sm:text-base">Préparation du fichier en cours...</span>
          </div>
        </div>
      )}

      {/* Modern Filters Panel */}
      {showFilters && (
        <Card className={`border-l-4 ${isDarkMode ? 'border-l-dark-primary-500' : 'border-l-primary-500'}`}>
          <div className="p-4 sm:p-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4">
              <div className="flex items-center mb-2 sm:mb-0">
                <div className="p-2 bg-primary-100 rounded-lg mr-3">
                  <i className="fas fa-filter text-primary-600"></i>
                </div>
                <div>
                  <h3 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    Filtres de recherche
                  </h3>
                  <p className="text-sm text-gray-600">Affinez votre recherche d'articles</p>
                </div>
              </div>

              <button
                onClick={() => setShowFilters(false)}
                className="self-end sm:self-auto text-gray-400 hover:text-gray-600 p-1"
              >
                <i className="fas fa-times"></i>
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              {/* Search Terms */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-white">
                  <i className="fas fa-search mr-2 text-gray-400"></i>
                  Recherche générale
                </label>
                <input
                  type="text"
                  value={filters.searchTerms}
                  onChange={(e) => setFilters(f => ({ ...f, searchTerms: e.target.value }))}
                  placeholder="Nom, code, description..."
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:border-transparent transition-colors ${isDarkMode
                    ? 'bg-gray-800 border-gray-600 text-white placeholder-gray-400 focus:ring-dark-primary-500'
                    : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500 focus:ring-primary-500'
                    }`}
                />
              </div>

              {/* Client (admin only) */}
              {user?.isAdmin && (
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-white">
                    <i className="fas fa-user mr-2 text-gray-400"></i>
                    Client
                  </label>
                  <ClientSelectDropdown
                    value={filters.ClientId}
                    onChange={val => setFilters(f => ({ ...f, ClientId: val, LocalId: '' }))}
                    options={clients}
                    loading={clientsLoading}
                    disabled={clientsLoading}
                    isDarkMode={isDarkMode}
                    placeholder="Tous les clients"
                    required={false}
                  />
                </div>
              )}

              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-white">
                  <i className="fas fa-building mr-2 text-gray-400"></i>
                  Local
                </label>
                <LocalSelectDropdown
                  value={filters.LocalId}
                  onChange={val => setFilters(f => ({ ...f, LocalId: val }))}
                  options={locaux}
                  loading={locauxLoading}
                  disabled={locauxLoading}
                  isDarkMode={isDarkMode}
                  placeholder="Tous les locaux"
                  required={false}
                />
              </div>

              {/* Code à barres with responsive camera scanner */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-white">
                  <i className="fas fa-barcode mr-2 text-gray-400"></i>
                  Code à barres
                </label>

                {/* Responsive flex container */}
                <div className="flex flex-col sm:flex-row gap-2 sm:gap-2">
                  <input
                    type="text"
                    value={filters.CodeABarre}
                    onChange={e => {
                      const value = e.target.value;
                      // Validate length on input
                      if (value.length <= 8) {
                        setFilters(f => ({ ...f, CodeABarre: value }));
                      } else {
                        toast.error('❌ Code invalide! Les codes-barres doivent faire 8 chiffres maximum.');
                      }
                    }}
                    placeholder="Code à barres (max 8 chiffres)"
                    maxLength={8}
                    className={`w-full sm:flex-1 px-3 py-2.5 sm:py-2 border rounded-lg focus:ring-2 focus:border-transparent transition-colors text-base sm:text-sm ${isDarkMode
                      ? 'bg-gray-800 border-gray-600 text-white placeholder-gray-400 focus:ring-dark-primary-500'
                      : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500 focus:ring-primary-500'
                      }`}
                  />

                  {/* Responsive button */}
                  <button
                    type="button"
                    onClick={() => {
                      if (barcodeScanning) {
                        stopScanning();
                      } else {
                        startBarcodeScanning();
                      }
                    }}
                    className={`w-full sm:w-auto px-4 py-2.5 sm:px-3 sm:py-2 rounded-lg font-medium transition-colors flex items-center justify-center gap-2 ${barcodeScanning
                        ? 'bg-red-600 text-white hover:bg-red-700'
                        : isDarkMode
                          ? 'bg-primary-700 text-white hover:bg-primary-800'
                          : 'bg-primary-600 text-white hover:bg-primary-700'
                      }`}
                    title={barcodeScanning ? "Arrêter le scanner" : "Scanner avec la caméra"}
                    disabled={barcodeScanning}
                  >
                    <i className={`fas ${barcodeScanning ? 'fa-stop' : 'fa-camera'} ${barcodeScanning ? 'animate-pulse' : ''}`}></i>
                    <span className="text-sm font-medium">
                      {barcodeScanning ? 'Arrêter le scanner' : 'Scanner le code'}
                    </span>
                  </button>
                </div>

                {/* Enhanced Camera Scanner Status */}
                {barcodeScanning && (
                  <div className={`mt-3 p-3 sm:p-4 rounded-lg border-2 border-dashed ${isDarkMode
                      ? 'border-primary-700 bg-gray-900 text-primary-300'
                      : 'border-primary-500 bg-primary-50 text-primary-700'
                    } text-center`}>
                    <div className="flex flex-col items-center gap-2">
                      <div className="flex items-center gap-2">
                        <i className="fas fa-camera animate-pulse text-lg"></i>
                        <span className="text-sm sm:text-base font-medium">
                          Scanner activé
                        </span>
                      </div>
                      <p className="text-xs sm:text-sm opacity-90">
                        Pointez votre caméra vers le code-barres
                      </p>
                      <div className="text-xs opacity-75 mt-1">
                        <span className="hidden sm:inline">Assurez-vous que le code est bien visible et éclairé</span>
                        <span className="sm:hidden">Code bien visible et éclairé</span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Enhanced validation indicator */}
                {filters.CodeABarre && filters.CodeABarre.length > 8 && (
                  <div className="mt-2 p-2 rounded-md bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
                    <div className="flex items-start gap-2 text-red-600 dark:text-red-400">
                      <i className="fas fa-exclamation-triangle mt-0.5 flex-shrink-0"></i>
                      <div className="text-xs sm:text-sm">
                        <p className="font-medium">Code trop long</p>
                        <p className="opacity-90">Maximum 8 chiffres autorisés</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Optional: Success indicator when valid */}
                {filters.CodeABarre && filters.CodeABarre.length > 0 && filters.CodeABarre.length <= 8 && (
                  <div className="mt-1 text-xs text-green-600 dark:text-green-400 flex items-center gap-1">
                    <i className="fas fa-check-circle"></i>
                    <span>Code valide ({filters.CodeABarre.length}/8 chiffres)</span>
                  </div>
                )}
              </div>

              {/* Numéro de série */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-white">
                  <i className="fas fa-hashtag mr-2 text-gray-400"></i>
                  Numéro de série
                </label>
                <input
                  type="text"
                  value={filters.NumeroDeSerie}
                  onChange={e => setFilters(f => ({ ...f, NumeroDeSerie: e.target.value }))}
                  placeholder="Numéro de série"
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:border-transparent transition-colors ${isDarkMode
                    ? 'bg-gray-800 border-gray-600 text-white placeholder-gray-400 focus:ring-dark-primary-500'
                    : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500 focus:ring-primary-500'
                    }`}
                />
              </div>
            </div>

            {/* Filter Actions */}
            <div className="flex flex-col sm:flex-row gap-3 sm:justify-end">
              <button
                onClick={clearFilters}
                className="px-4 py-2 text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors text-sm font-medium order-2 sm:order-1"
              >
                <i className="fas fa-times mr-2"></i>
                Effacer
              </button>
              <button
                onClick={applyFilters}
                className="px-6 py-2 bg-primary-600 text-white hover:bg-primary-700 rounded-lg transition-colors text-sm font-medium order-1 sm:order-2"
              >
                <i className="fas fa-search mr-2"></i>
                Appliquer les filtres
              </button>
            </div>

            {/* Active Filters Summary */}
            {filtersApplied && (
              <div className="mt-4 pt-4 border-t border-gray-200">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-sm text-gray-600">Filtres actifs:</span>
                  {filters.searchTerms && (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-800">
                      <i className="fas fa-search mr-1"></i>
                      {filters.searchTerms}
                      <button
                        onClick={() => handleFilterChange('searchTerms', '')}
                        className="ml-1 text-primary-600 hover:text-primary-800"
                      >
                        <i className="fas fa-times text-xs"></i>
                      </button>
                    </span>
                  )}
                  {filters.LocalId && (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-800">
                      <i className="fas fa-map-marker-alt mr-1"></i>
                      Local: {filters.LocalId}
                      <button
                        onClick={() => handleFilterChange('LocalId', '')}
                        className="ml-1 text-primary-600 hover:text-primary-800"
                      >
                        <i className="fas fa-times text-xs"></i>
                      </button>
                    </span>
                  )}
                  {filters.ClientId && (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-800">
                      <i className="fas fa-user mr-1"></i>
                      Client: {filters.ClientId}
                      <button
                        onClick={() => handleFilterChange('ClientId', '')}
                        className="ml-1 text-primary-600 hover:text-primary-800"
                      >
                        <i className="fas fa-times text-xs"></i>
                      </button>
                    </span>
                  )}
                  {filters.CodeABarre && (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-800">
                      <i className="fas fa-barcode mr-1"></i>
                      Code: {filters.CodeABarre}
                      <button
                        onClick={() => handleFilterChange('CodeABarre', '')}
                        className="ml-1 text-primary-600 hover:text-primary-800"
                      >
                        <i className="fas fa-times text-xs"></i>
                      </button>
                    </span>
                  )}
                  {filters.NumeroDeSerie && (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-800">
                      <i className="fas fa-hashtag mr-1"></i>
                      Série: {filters.NumeroDeSerie}
                      <button
                        onClick={() => handleFilterChange('NumeroDeSerie', '')}
                        className="ml-1 text-primary-600 hover:text-primary-800"
                      >
                        <i className="fas fa-times text-xs"></i>
                      </button>
                    </span>
                  )}
                </div>
              </div>
            )}
          </div>
        </Card>
      )}

      {/* Articles List - Responsive */}
      <ArticlesList
        articles={articles}
        loading={loading}
        pagination={pagination}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        onView={handleView}
        onEdit={handleEdit}
        onDelete={handleDelete}
      />

      {/* Modals */}
      {/* Modals: Only show Add/Edit/Delete for admin, View for all */}
      {user?.isAdmin && (
        <AddArticleModal
          isOpen={showAddModal}
          onClose={() => setShowAddModal(false)}
          onSuccess={() => {
            setShowAddModal(false);
            handleRefresh();
          }}
        />
      )}

      {user?.isAdmin && (
        <EditArticleModal
          isOpen={showEditModal}
          article={selectedArticle}
          onClose={() => {
            setShowEditModal(false);
            setSelectedArticle(null);
          }}
          onSuccess={() => {
            setShowEditModal(false);
            setSelectedArticle(null);
            handleRefresh();
          }}
        />
      )}

      <ViewArticleModal
        isOpen={showViewModal}
        article={selectedArticle}
        onClose={() => {
          setShowViewModal(false);
          setSelectedArticle(null);
        }}
      />

      {user?.isAdmin && (
        <DeleteArticleModal
          isOpen={showDeleteModal}
          article={selectedArticle}
          onClose={() => {
            setShowDeleteModal(false);
            setSelectedArticle(null);
          }}
          onSuccess={() => {
            setShowDeleteModal(false);
            setSelectedArticle(null);
            handleRefresh();
          }}
        />
      )}

      {/* Click outside to close mobile actions */}
      {showMobileActions && (
        <div
          className="fixed inset-0 z-0"
          onClick={() => setShowMobileActions(false)}
        />
      )}
    </div>
  );
};

export default Articles;